import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router'
import { AppointmentType, RouteParameters, SearchParameters } from '../../../constants'
import { useDoubleBookingContext, useFeatureFlags, useRescheduleContext, useSearchParameters } from '../../../hooks'
import { AppointmentFormData } from '../../../types'
import { logFactory } from '../../../util'

type FormActionMode = 'loading' | 'no-action' | 'reschedule' | 'schedule'

const log = logFactory('useFormActionMode')

const useFormActionMode = (): FormActionMode => {
  const [preventDoubleBookingHearingAidEvaluations] = useFeatureFlags<boolean>(['PREVENT_DOUBLE_BOOKING_HEARING_AID_EVALUATIONS'])
  const { isLoading, hasCompletedHearingAidEvaluation, hasConfirmedHearingAidEvaluation } = useDoubleBookingContext()
  const { [SearchParameters.PREVIEW]: searchParamIsPreview } = useSearchParameters()
  const isPreview = searchParamIsPreview?.toString().trim().toLowerCase() === 'true'
  const { [RouteParameters.APPOINTMENT_ID]: routeParamAppointmentId } = useParams()
  const isSpecificAppointmentFlow = Boolean(routeParamAppointmentId)
  const rescheduleContext = useRescheduleContext()
  const isRescheduling = Boolean(rescheduleContext?.reason)
  const formContext = useFormContext<AppointmentFormData>()
  const selectedAppointmentType = formContext?.watch('type')
  const isHearingAidEvaluationScheduling = selectedAppointmentType === AppointmentType.HEARING_AID_EVALUATION

  log('📋 Form action mode conditions...', {
    isLoading,
    isPreview,
    isSpecificAppointmentFlow,
    isRescheduling,
    isHearingAidEvaluationScheduling,
    preventDoubleBookingHearingAidEvaluations,
    hasCompletedHearingAidEvaluation,
    hasConfirmedHearingAidEvaluation,
  })

  if (isLoading) {
    // Indeterminate state while loading
    return 'no-action'
  }

  if (isPreview) {
    // The user is explicitly previewing availabilities
    return 'no-action'
  }

  if (isSpecificAppointmentFlow) {
    // The user is performing a action on an existing appointment (e.g. cancelling, rescheduling)
    return 'no-action'
  }

  if (!isHearingAidEvaluationScheduling) {
    // Allow the user unrestricted scheduling of appointments that are NOT hearing aid evaluations
    return 'schedule'
  }

  if (!preventDoubleBookingHearingAidEvaluations) {
    // Allow the user to schedule an appointment without checking for double bookings
    return 'schedule'
  }

  if (isRescheduling) {
    // The user has chosen to reschedule the given appointment, let them schedule normally
    return 'schedule'
  }

  if (hasCompletedHearingAidEvaluation) {
    // The user can only book one hearing aid evaluation per opportunity
    return 'no-action'
  }

  if (hasConfirmedHearingAidEvaluation) {
    // If the user has confirmed a hearing aid evaluation, they are allowed to reschedule it
    return 'reschedule'
  }

  if (!hasConfirmedHearingAidEvaluation) {
    // If the user doesn't have a confirmed a hearing aid evaluation, they may schedule one
    return 'schedule'
  }

  return 'no-action'
}

export { useFormActionMode }
export type { FormActionMode }
